import React, { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

interface DataPoint {
  timestamp: string | number;
  value?: number | null;
  avg_value?: number | null;
  [key: string]: unknown;
}

interface ProcessedDataPoint {
  timestamp: string | number;
  value: number | null;
  [key: string]: unknown;
}

interface Props {
  data?: DataPoint[];
  title?: string;
  yLabel?: string;
  unit?: string;
  color?: string;
  gradientId?: string;
  height?: number;
  warningThreshold?: number | null;
  criticalThreshold?: number | null;
  showGrid?: boolean;
  showLegend?: boolean;
}

const formatTimestamp = (timestamp: string | number): string => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

const formatValue = (value: number | null | undefined, unit: string): string => {
  if (value == null) return '--';
  if (unit === '%') return `${value.toFixed(1)}%`;
  if (unit === 'ms') return `${value.toFixed(1)}ms`;
  if (unit === 'bytes') {
    if (value >= 1073741824) return `${(value / 1073741824).toFixed(2)} GB`;
    if (value >= 1048576) return `${(value / 1048576).toFixed(2)} MB`;
    if (value >= 1024) return `${(value / 1024).toFixed(2)} KB`;
    return `${value} B`;
  }
  return value.toFixed(2);
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value?: number | null }>;
  label?: string | number;
  unit: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;

  const data = payload[0];
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
        {formatTimestamp(label as string | number)}
      </p>
      <p className="text-sm font-medium text-gray-900 dark:text-white">
        {formatValue(data.value, unit)}
      </p>
    </div>
  );
};

const APMTimeseriesChart: React.FC<Props> = ({
  data = [],
  title,
  yLabel,
  unit = '%',
  color = '#3b82f6',
  gradientId,
  height = 200,
  warningThreshold,
  criticalThreshold,
  showGrid = true,
}) => {
  const chartId = useMemo(() => gradientId ?? `gradient-${Math.random().toString(36).substr(2, 9)}`, [gradientId]);

  const processedData = useMemo((): ProcessedDataPoint[] => {
    if (!data || data.length === 0) return [];
    return data.map((point) => ({
      ...point,
      timestamp: point.timestamp,
      value: point.value ?? point.avg_value ?? null,
    }));
  }, [data]);

  const yDomain = useMemo((): [number, number] => {
    if (!processedData.length) return [0, 100];
    const values = processedData.map(d => d.value).filter((v): v is number => v != null && !Number.isNaN(Number(v)));
    if (values.length === 0) return [0, 100];

    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.1 || 10;

    if (unit === '%') {
      return [Math.max(0, min - padding), Math.min(100, max + padding)];
    }

    return [Math.max(0, min - padding), max + padding];
  }, [processedData, unit]);

  const hasActualData = useMemo(() => {
    return processedData.some(d => d.value != null);
  }, [processedData]);

  if (!processedData || processedData.length === 0 || !hasActualData) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow text-gray-500 dark:text-gray-400">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">{title}</h3>
        <div
          className="flex items-center justify-center text-gray-500 dark:text-gray-400"
          style={{ height }}
        >
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow text-gray-500 dark:text-gray-400">
      {title && (
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
          {title}
        </h3>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={processedData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <defs>
            <linearGradient id={chartId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0.05} />
            </linearGradient>
          </defs>

          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e5e7eb"
              className="dark:stroke-gray-700"
            />
          )}

          <XAxis
            dataKey="timestamp"
            tickFormatter={formatTimestamp}
            tick={{ fontSize: 11, fill: 'currentColor' }}
            tickLine={{ stroke: '#e5e7eb' }}
            axisLine={{ stroke: '#e5e7eb' }}
            minTickGap={50}
          />

          <YAxis
            domain={yDomain}
            tick={{ fontSize: 11, fill: 'currentColor' }}
            tickLine={{ stroke: '#e5e7eb' }}
            axisLine={{ stroke: '#e5e7eb' }}
            tickFormatter={(value: number) => formatValue(value, unit)}
            width={60}
          />

          <Tooltip content={<CustomTooltip unit={unit} />} />

          {/* Warning threshold line */}
          {warningThreshold != null && (
            <ReferenceLine
              y={warningThreshold}
              stroke="#f59e0b"
              strokeDasharray="5 5"
              strokeWidth={1}
              label={{
                value: 'Warning',
                position: 'right',
                fill: '#f59e0b',
                fontSize: 10,
              }}
            />
          )}

          {/* Critical threshold line */}
          {criticalThreshold != null && (
            <ReferenceLine
              y={criticalThreshold}
              stroke="#ef4444"
              strokeDasharray="5 5"
              strokeWidth={1}
              label={{
                value: 'Critical',
                position: 'right',
                fill: '#ef4444',
                fontSize: 10,
              }}
            />
          )}

          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${chartId})`}
            activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>

      {yLabel && (
        <div className="text-center mt-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">{yLabel}</span>
        </div>
      )}
    </div>
  );
};

export default APMTimeseriesChart;
